"use strict";

import { DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN, DATADOG_SERVICE, DATADOG_SITE } from '@autoguru/datadog-config';
import { ENV_NAME } from '@autoguru/global-configs';
import { reactPlugin } from '@datadog/browser-rum-react';
import { getAllowedTracingUrls } from './traceConfig';
const VERSION = '1.0.0';

/**
 * Portal-specific base sampling rates.
 * Fleet portals get highest sampling (100% session) for comprehensive monitoring.
 * Consumer portals (MY_GARAGE, DE_PORTAL) get medium sampling (20% session).
 * Supplier portal and legacy MFEs get lowest sampling (10% session) for cost optimisation.
 */
const PORTAL_BASE_RATES = {
  FLEET_PORTAL: {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 5
  },
  FLEET_CLIENT_PORTAL: {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 5
  },
  MY_GARAGE: {
    sessionSampleRate: 20,
    sessionReplaySampleRate: 5
  },
  DE_PORTAL: {
    sessionSampleRate: 20,
    sessionReplaySampleRate: 5
  },
  SUPPLIER_PORTAL: {
    sessionSampleRate: 10,
    sessionReplaySampleRate: 2
  },
  DEFAULT: {
    sessionSampleRate: 10,
    sessionReplaySampleRate: 2
  }
};

/**
 * Mode multipliers applied on top of portal base rates.
 * - minimal: 0.2x for cost reduction
 * - default: 1.0x (uses portal base rates as-is)
 * - high-fidelity: 5x replay for debugging (session stays at 1x)
 */
const MODE_MULTIPLIERS = {
  minimal: {
    session: 0.2,
    replay: 0.2
  },
  default: {
    session: 1.0,
    replay: 1.0
  },
  'high-fidelity': {
    session: 1.0,
    replay: 5.0
  }
};

/**
 * Calculates sampling rates based on portal identity and feature flag mode.
 * Combines portal-specific base rates with mode multipliers, capping at 100%.
 *
 * @param portal - The portal/feature name (e.g., 'FLEET_PORTAL', 'MY_GARAGE', 'na')
 * @param mode - The DataDog mode from feature flags
 * @returns Sampling configuration with sessionSampleRate and sessionReplaySampleRate
 */
export const getSamplingConfig = (portal, mode) => {
  var _PORTAL_BASE_RATES$po;
  const baseRates = (_PORTAL_BASE_RATES$po = PORTAL_BASE_RATES[portal]) !== null && _PORTAL_BASE_RATES$po !== void 0 ? _PORTAL_BASE_RATES$po : PORTAL_BASE_RATES.DEFAULT;
  const multipliers = MODE_MULTIPLIERS[mode];
  return {
    sessionSampleRate: Math.min(100, Math.round(baseRates.sessionSampleRate * multipliers.session)),
    sessionReplaySampleRate: Math.min(100, Math.round(baseRates.sessionReplaySampleRate * multipliers.replay))
  };
};
export const datadogOptions = {
  applicationId: DATADOG_APPLICATION_ID,
  clientToken: DATADOG_CLIENT_TOKEN,
  site: DATADOG_SITE,
  service: DATADOG_SERVICE,
  env: ENV_NAME,
  version: VERSION,
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  defaultPrivacyLevel: 'mask-user-input',
  trackingConsent: 'granted',
  plugins: [reactPlugin({
    router: false
  })],
  beforeSend: _event => true,
  allowedTracingUrls: getAllowedTracingUrls()
};

/**
 * DataDog Logs SDK configuration for 100% error capture.
 *
 * Per Datadog: "RUM session sampling only affects RUM events and session replays.
 * Front-end logs can be collected independently at 100%."
 *
 * - forwardErrorsToLogs: captures uncaught exceptions & network errors
 * - forwardConsoleLogs: captures console.error calls
 *
 * When in a sampled RUM session, logs automatically correlate.
 * When not sampled, logs still captured but without session context.
 */
export const datadogLogsOptions = {
  clientToken: DATADOG_CLIENT_TOKEN,
  site: DATADOG_SITE,
  service: DATADOG_SERVICE,
  env: ENV_NAME,
  version: VERSION,
  forwardErrorsToLogs: true,
  forwardConsoleLogs: ['error'],
  sessionSampleRate: 100,
  telemetrySampleRate: 0
};